// @ts-nocheck

import { useState } from 'react';
import { FilePlus2, FileText, Globe, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog } from '@/components/ui/dialog';
import { KbDialogContent, KbDialogHead, KbFileDrop, ErrMsg } from './Kbdialogparts';
import { addKnowledgeBaseDocument } from '@/service/knowledgeBaseApi';
import { KNOWLEDGE_BASE_MAX_FILE_SIZE_BYTES, KNOWLEDGE_BASE_ACCEPT } from '@/utilities/constant';
import { validateKnowledgeBaseFile } from '@/utilities/knowledgeBaseFile';

const SOURCE_TYPES = [
  { value: 'file', label: 'Upload file', hint: 'PDF, DOC, DOCX, TXT' },
  { value: 'url', label: 'Website / URL', hint: 'Crawl a public page' },
];

const MAX_FILE_LABEL = `${(KNOWLEDGE_BASE_MAX_FILE_SIZE_BYTES / 1024 / 1024).toFixed(0)} MB`;

export default function AddDocumentDialog({ open, onOpenChange, kbId, kbName, onAdded }: any) {
  const [sourceType, setSourceType] = useState('file');
  const [url, setUrl] = useState('');
  const [file, setFile] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const reset = () => {
    setSourceType('file');
    setUrl('');
    setFile([]);
    setError('');
  };

  const close = () => {
    reset();
    onOpenChange(false);
  };

  const handleFileChange = async (files: File[]) => {
    if (!files || files.length === 0) {
      setFile([]);
      return;
    }
    const fileError = await validateKnowledgeBaseFile(files[0]);
    if (fileError) {
      setFile([]);
      setError(fileError);
      toast.error(fileError);
      return;
    }
    setFile(files);
    setError('');
  };

  const handleSubmit = async () => {
    if (sourceType === 'url' && !url.trim()) {
      setError('Enter a URL to ingest');
      return;
    }
    if (sourceType === 'file' && file.length === 0) {
      setError('Choose a file to upload');
      return;
    }
    if (sourceType === 'file') {
      const fileError = await validateKnowledgeBaseFile(file[0]);
      if (fileError) {
        setError(fileError);
        toast.error(fileError);
        return;
      }
    }
    setError('');
    setSaving(true);
    try {
      await addKnowledgeBaseDocument(kbId, {
        url: sourceType === 'url' ? url.trim() : undefined,
        file: sourceType === 'file' ? file[0] : undefined,
      });
      toast.success('Document queued for ingestion');
      close();
      onAdded?.();
    } catch (err: any) {
      toast.error(typeof err === 'string' ? err : 'Failed to add document');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? close() : onOpenChange(o))}>
      <KbDialogContent className="sm:max-w-lg">
        <KbDialogHead
          icon={FilePlus2}
          title="Add document"
          sub={<>Add another source to <b style={{ color: 'var(--ink)', fontWeight: 600 }}>{kbName || 'this knowledge base'}</b>.</>}
        />

        <div className="ags-kbopts" role="radiogroup" aria-label="Source type" style={{ marginBottom: 16 }}>
          {SOURCE_TYPES.map((opt) => (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={sourceType === opt.value}
              className="ags-kbopt"
              onClick={() => {
                setSourceType(opt.value);
                setError('');
              }}
            >
              <span className="ags-dot">{opt.value === 'file' ? <FileText className="ags-i" /> : <Globe className="ags-i" />}</span>
              <span>
                <b>{opt.label}</b>
                <small>{opt.hint}</small>
              </span>
            </button>
          ))}
        </div>

        {sourceType === 'url' ? (
          <div className="ags-field" style={{ marginBottom: 0 }}>
            <label htmlFor="add-doc-url">
              URL <span className="ags-r">*</span>
            </label>
            <input
              id="add-doc-url"
              className={`ags-ctl${error ? ' ags-invalid' : ''}`}
              placeholder="https://example.com/help"
              value={url}
              autoComplete="off"
              onChange={(e) => {
                setUrl(e.target.value);
                if (error) setError('');
              }}
            />
            <ErrMsg>{error}</ErrMsg>
          </div>
        ) : (
          <div className="ags-field" style={{ marginBottom: 0 }}>
            <label>
              File <span className="ags-r">*</span>
            </label>
            <KbFileDrop file={file} onChange={handleFileChange} onError={setError} invalid={!!error} />
            <ErrMsg>{error}</ErrMsg>
          </div>
        )}

        <div className="ags-dlgfoot">
          <button type="button" className="ags-btn" onClick={close} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="ags-btn ags-primary" onClick={handleSubmit} disabled={saving}>
            {saving ? <Loader2 className="ags-i animate-spin" /> : <Check className="ags-i" />}
            Add document
          </button>
        </div>
      </KbDialogContent>
    </Dialog>
  );
}
